<script setup>
// X182 / X204 (R83 L1): "Not published yet": an unpublished competition's Copy / Share / Publish ask first ("Publish and
// copy" / "Copy anyway" / Cancel). One gate for every place that hands out the link: the Invite settings dialog and the
// empty-pool invite card (InviteMorePlayers). v-model is the ask: null | {mode: 'publish' | 'copy' | 'share', share?: Function}.
// `proceed` (with the ask) once the admin chose to go on (Copy anyway, or published): the host copies / shares.
import TlDialog from '~/components/TlDialog.vue';
import {useCompetitionPublish} from '~/composables/useCompetitionPublish';
import {entryFeeImpactText} from '~/utils/adminActionCopy';

const props = defineProps({
  referenceType: {type: Number, required: true},
  referenceId: {type: String, required: true},
  entityName: {type: String, default: ''},
  playersPerTeam: {type: Number, default: 1},
});
const gate = defineModel({type: Object, default: null});
const emit = defineEmits(['proceed', 'published']);

const {setAlertMessage} = useAlertService();
const {publishByReference} = useCompetitionPublish();
const tournamentApi = useCrudApi('api/v2/tournaments');
const cycleApis = {2: useCrudApi('api/v2/leagueCycles'), 6: useCrudApi('api/v2/americanoCycles')};
const open = computed({get: () => gate.value != null, set: (v) => { if (!v) gate.value = null; }});
const publishing = ref(false);
const publishErrors = ref(null);
const feeImpact = ref(null);

const loadFeeImpact = async () => {
  feeImpact.value = null;
  try {
    if (props.referenceType === 4) {
      await tournamentApi.getItem(props.referenceId);
      feeImpact.value = tournamentApi.item.value?.entryFeeImpact ?? null;
    } else if (cycleApis[props.referenceType]) {
      const api = cycleApis[props.referenceType];
      await api.getItem(props.referenceId);
      feeImpact.value = api.item.value?.entryFeeImpact ?? null;
    }
  } catch { feeImpact.value = null; }
};
watch(() => gate.value, (g, before) => {
  if (g && !before) {
    publishErrors.value = null;
    loadFeeImpact();
  }
});
const feeText = computed(() => entryFeeImpactText(feeImpact.value,
    (props.playersPerTeam ?? 1) > 1 && props.referenceType !== 6 ? 'team' : 'player'));

const anyway = () => {
  const g = gate.value;
  gate.value = null;
  if (g) emit('proceed', g);
};
const publish = async () => {
  const g = gate.value;
  publishing.value = true;
  publishErrors.value = null;
  try {
    const result = await publishByReference(props.referenceType, props.referenceId, async (id) => {
      await tournamentApi.getItem(id);
      return tournamentApi.item.value;
    });
    if (!result.ok) {
      publishErrors.value = result.errors || {global: ['Publishing failed. Please try again.']};
      return;
    }
    gate.value = null;
    setAlertMessage(`${props.entityName || 'Competition'} published`);
    emit('published');
    if (g && g.mode !== 'publish') emit('proceed', g);
  } finally {
    publishing.value = false;
  }
};
const primary = computed(() => ({
  publish: feeText.value ? 'Publish and bill' : 'Publish',
  copy: 'Publish and copy',
  share: 'Publish and share',
}[gate.value?.mode ?? 'publish']));
const anywayLabel = computed(() => ({copy: 'Copy anyway', share: 'Share anyway'}[gate.value?.mode] ?? null));
</script>

<template>
  <!-- X201 (R81 L6): the line no longer repeats the title; wide enough for the three buttons on one row on desktop -->
  <tl-dialog v-model="open" title="Not published yet" :max-width="520">
    <p class="jpg-line" data-testid="join-unpublished-gate">
      Anyone who opens the link can't join until you publish.
    </p>
    <v-alert v-if="feeText" type="warning" variant="tonal" density="compact" class="mt-3">
      {{ feeText }} No card is charged at this point.
    </v-alert>
    <crud-error-display v-if="publishErrors" :errors="publishErrors" show-all class="mt-3"/>
    <template #actions>
      <v-btn variant="text" :disabled="publishing" @click="gate = null">Cancel</v-btn>
      <v-spacer/>
      <v-btn v-if="anywayLabel" variant="outlined" :disabled="publishing" @click="anyway">{{ anywayLabel }}</v-btn>
      <v-btn color="primary" variant="flat" prepend-icon="mdi-publish" :loading="publishing" @click="publish">{{ primary }}</v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped>
.jpg-line {
  font-size: 13.5px;
  line-height: 1.6;
  margin: 0;
}
</style>
